import { generateRouteElements } from '@/components/layout/AppRoutes/AppRoutes';
import type React from 'react';
import { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Routes } from 'react-router';
import { ROUTES } from '../../../constants/Constants';
import { lazyReactNaiveRetry } from '../../../constants/Lazy';
import useUser from '../../../hooks/useUser';
import { getPartnerDetailsRequest } from '../../../redux/ducks/DashboardDucks';
import type { GetPartnerDetailsResponseDataType } from '../../../redux/ducks/DashboardDucks.types';
import {
  setDashboardMobileSideMenuOpen,
  setFirstDashboardAccess,
} from '../../../redux/ducks/UIDucks';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import type { AppSsrFetchedDataType } from '../../../server/AppSsr';
import DashboardBottomMobileMenu from '../../layout/Dashboard/DashboardBottomMobileMenu';
import DashboardSideMenuControlPanel from '../../layout/Dashboard/DashboardSideMenuControlPanel';
import DashboardSideMenuPanel from '../../layout/Dashboard/DashboardSideMenuPanel';
import DashboardLayout from '../../layout/DashboardLayout/DashboardLayout';
import Spinner from '../../layout/Spinner/Spinner';

const ClientDetailsSection = lazyReactNaiveRetry(
  () => import('../../layout/Dashboard/sections/ClientDetailsSection'),
);
const ServiceDetailsSection = lazyReactNaiveRetry(
  () => import('../../layout/Dashboard/sections/ServiceDetailsSection'),
);
const BusinessDetailsSection = lazyReactNaiveRetry(
  () => import('../../layout/Dashboard/sections/BusinessDetailsSection'),
);
const TempHoursSection = lazyReactNaiveRetry(
  () => import('../../layout/Dashboard/sections/TempHoursSection'),
);
const ProfileSection = lazyReactNaiveRetry(
  () => import('../../layout/Dashboard/sections/ProfileSection'),
);
const DashboardSection = lazyReactNaiveRetry(
  () => import('../../layout/Dashboard/sections/DashboardSection'),
);
const BusinessSection = lazyReactNaiveRetry(
  () => import('../../layout/Dashboard/sections/BusinessSection'),
);
const ServiceSection = lazyReactNaiveRetry(
  () => import('../../layout/Dashboard/sections/ServiceSection'),
);
const AppointmentsSection = lazyReactNaiveRetry(
  () => import('../../layout/Dashboard/sections/AppointmentsSection'),
);
const ClientsSection = lazyReactNaiveRetry(
  () => import('../../layout/Dashboard/sections/ClientsSection'),
);
const SettingsSection = lazyReactNaiveRetry(
  () => import('../../layout/Dashboard/sections/SettingsSection'),
);
const AppointmentDetailsSection = lazyReactNaiveRetry(
  () => import('../../layout/Dashboard/sections/AppointmentDetailsSection'),
);
const TreatmentDetailsSection = lazyReactNaiveRetry(
  () => import('../../layout/Dashboard/sections/TreatmentDetailsSection'),
);

const DashboardNewAppointmentModal = lazyReactNaiveRetry(
  () => import('../../modals/DashboardNewAppointamentModal'),
);
const DashboardMoveAppointmentModal = lazyReactNaiveRetry(
  () => import('../../modals/DashboardMoveAppointmentModal'),
);
const DashboardNewBusinessModal = lazyReactNaiveRetry(
  () => import('../../modals/DashboardNewBusinessModal'),
);
const DashboardNewServiceModal = lazyReactNaiveRetry(
  () => import('../../modals/DashboardNewServiceModal'),
);
const DashboardNewTempHourModal = lazyReactNaiveRetry(
  () => import('../../modals/DashboardNewTempHourModal'),
);
const DashboardNewContactModal = lazyReactNaiveRetry(
  () => import('../../modals/DashboardNewContactModal'),
);
const DashboardCancelAppointmentModal = lazyReactNaiveRetry(
  () => import('../../modals/DashboardCancelAppointmentModal'),
);
const DashboardCompleteAppointmentModal = lazyReactNaiveRetry(
  () => import('../../modals/DashboardCompleteAppointmentModal'),
);
const ProfilePictureModal = lazyReactNaiveRetry(
  () => import('../../modals/ProfilePictureModal'),
);

interface DashboardProps {
  ssrFetchedData: AppSsrFetchedDataType;
}

const Dashboard: React.FC<DashboardProps> = ({ ssrFetchedData }) => {
  const dispatch = useDispatch();
  const { currentUser } = useUser();
  const [partnerDetails, setPartnerDetails] = useState<
    GetPartnerDetailsResponseDataType | undefined
  >();

  const { isDashboardMobileSideMenuOpen } = useSelector(
    (state: RootReducerType) => state.ui,
  );
  const { getPartnerDetailsResponse } = useSelector(
    (state: RootReducerType) => state.dashboard,
  );

  useEffect(() => {
    dispatch(setFirstDashboardAccess(false));

    return () => {
      dispatch(setDashboardMobileSideMenuOpen(false));
    };
  }, []);

  useEffect(() => {
    if (currentUser?.partner_id) {
      dispatch(
        getPartnerDetailsRequest({ userPartner: currentUser.partner_id }),
      );
    }
  }, [currentUser]);

  useEffect(() => {
    if (
      getPartnerDetailsResponse?.data &&
      getPartnerDetailsResponse.data.id === currentUser?.partner_id
    ) {
      setPartnerDetails(getPartnerDetailsResponse.data);
    }
  }, [getPartnerDetailsResponse, currentUser]);

  return partnerDetails ? (
    <DashboardLayout>
      <Suspense fallback={<div className="hidden">&nbsp;</div>}>
        <DashboardNewAppointmentModal partnerDetails={partnerDetails} />
      </Suspense>
      <Suspense fallback={<div className="hidden">&nbsp;</div>}>
        <DashboardMoveAppointmentModal partnerDetails={partnerDetails} />
      </Suspense>
      <Suspense fallback={<div className="hidden">&nbsp;</div>}>
        <DashboardCancelAppointmentModal partnerDetails={partnerDetails} />
      </Suspense>
      <Suspense fallback={<div className="hidden">&nbsp;</div>}>
        <DashboardCompleteAppointmentModal partnerDetails={partnerDetails} />
      </Suspense>
      <Suspense fallback={<div className="hidden">&nbsp;</div>}>
        <DashboardNewContactModal partnerDetails={partnerDetails} />
      </Suspense>
      <Suspense fallback={<div className="hidden">&nbsp;</div>}>
        <DashboardNewServiceModal partnerDetails={partnerDetails} />
      </Suspense>
      <Suspense fallback={<div className="hidden">&nbsp;</div>}>
        <DashboardNewTempHourModal partnerDetails={partnerDetails} />
      </Suspense>
      <Suspense fallback={<div className="hidden">&nbsp;</div>}>
        <DashboardNewBusinessModal partnerDetails={partnerDetails} />
      </Suspense>
      <Suspense fallback={<div className="hidden">&nbsp;</div>}>
        <ProfilePictureModal />
      </Suspense>
      <div className="relative flex min-h-full grow flex-row overflow-x-hidden">
        <div className="hidden min-h-full min-w-[260px] max-w-[260px] flex-col lg:flex">
          <DashboardSideMenuPanel onDesktop />
        </div>
        <div
          className={`${
            isDashboardMobileSideMenuOpen ? ' left-0' : 'left-[-310px]'
          } absolute top-0 flex min-h-full w-[310px] max-w-[310px] flex-col transition-all lg:hidden`}
        >
          <DashboardSideMenuPanel onDesktop={false} />
        </div>
        <div
          className={`${
            isDashboardMobileSideMenuOpen ? 'ml-[310px] min-w-full' : ''
          } relative flex grow flex-col transition-all`}
        >
          <DashboardSideMenuControlPanel />
          <div
            className={`grow ${
              isDashboardMobileSideMenuOpen && 'pointer-events-none'
            } flex flex-col`}
          >
            <Routes>
              {generateRouteElements(
                ROUTES.DASHBOARD_ROUTE.replace(ROUTES.DASHBOARD_ROUTE, ''),
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <AppointmentsSection partnerDetails={partnerDetails} />
                </Suspense>,
                ssrFetchedData,
              )}
              {generateRouteElements(
                ROUTES.DASHBOARD_ROUTE_OVERVIEW.replace(
                  ROUTES.DASHBOARD_ROUTE,
                  '',
                ),
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <DashboardSection partnerDetails={partnerDetails} />
                </Suspense>,
                ssrFetchedData,
              )}
              {generateRouteElements(
                ROUTES.DASHBOARD_ROUTE_PROFILE.replace(
                  ROUTES.DASHBOARD_ROUTE,
                  '',
                ),
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <ProfileSection partnerDetails={partnerDetails} />
                </Suspense>,
                ssrFetchedData,
              )}
              {generateRouteElements(
                ROUTES.DASHBOARD_ROUTE_BUSINESS.replace(
                  ROUTES.DASHBOARD_ROUTE,
                  '',
                ),
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <BusinessSection partnerDetails={partnerDetails} />
                </Suspense>,
                ssrFetchedData,
              )}
              {generateRouteElements(
                ROUTES.DASHBOARD_ROUTE_BUSINESS_DETAILS.replace(
                  ROUTES.DASHBOARD_ROUTE,
                  '',
                ),
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <BusinessDetailsSection partnerDetails={partnerDetails} />
                </Suspense>,
                ssrFetchedData,
              )}
              {generateRouteElements(
                ROUTES.DASHBOARD_ROUTE_SERVICE.replace(
                  ROUTES.DASHBOARD_ROUTE,
                  '',
                ),
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <ServiceSection partnerDetails={partnerDetails} />
                </Suspense>,
                ssrFetchedData,
              )}
              {generateRouteElements(
                ROUTES.DASHBOARD_ROUTE_SERVICE_DETAILS.replace(
                  ROUTES.DASHBOARD_ROUTE,
                  '',
                ),
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <ServiceDetailsSection partnerDetails={partnerDetails} />
                </Suspense>,
                ssrFetchedData,
              )}
              {generateRouteElements(
                ROUTES.DASHBOARD_ROUTE_APPOINTMENTS.replace(
                  ROUTES.DASHBOARD_ROUTE,
                  '',
                ),
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <AppointmentsSection partnerDetails={partnerDetails} />
                </Suspense>,
                ssrFetchedData,
              )}
              {generateRouteElements(
                ROUTES.DASHBOARD_ROUTE_TREATMENT_DETAILS.replace(
                  ROUTES.DASHBOARD_ROUTE,
                  '',
                ),
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <TreatmentDetailsSection partnerDetails={partnerDetails} />
                </Suspense>,
                ssrFetchedData,
              )}
              {generateRouteElements(
                ROUTES.DASHBOARD_ROUTE_APPOINTMENT_DETAILS.replace(
                  ROUTES.DASHBOARD_ROUTE,
                  '',
                ),
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <AppointmentDetailsSection partnerDetails={partnerDetails} />
                </Suspense>,
                ssrFetchedData,
              )}
              {generateRouteElements(
                ROUTES.DASHBOARD_ROUTE_CONTACTS.replace(
                  ROUTES.DASHBOARD_ROUTE,
                  '',
                ),
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <ClientsSection partnerDetails={partnerDetails} />
                </Suspense>,
                ssrFetchedData,
              )}
              {generateRouteElements(
                ROUTES.DASHBOARD_ROUTE_CONTACT_DETAILS.replace(
                  ROUTES.DASHBOARD_ROUTE,
                  '',
                ),
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <ClientDetailsSection partnerDetails={partnerDetails} />
                </Suspense>,
                ssrFetchedData,
              )}
              {generateRouteElements(
                ROUTES.DASHBOARD_ROUTE_SETTINGS.replace(
                  ROUTES.DASHBOARD_ROUTE,
                  '',
                ),
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <SettingsSection partnerDetails={partnerDetails} />
                </Suspense>,
                ssrFetchedData,
              )}
              {generateRouteElements(
                ROUTES.DASHBOARD_ROUTE_TEMP_CLOSING_HOURS.replace(
                  ROUTES.DASHBOARD_ROUTE,
                  '',
                ),
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <TempHoursSection partnerDetails={partnerDetails} />
                </Suspense>,
                ssrFetchedData,
              )}
              {generateRouteElements(
                ROUTES.DASHBOARD_ROUTE_TEMP_OPENING_HOURS.replace(
                  ROUTES.DASHBOARD_ROUTE,
                  '',
                ),
                <Suspense fallback={<div className="hidden">&nbsp;</div>}>
                  <TempHoursSection partnerDetails={partnerDetails} />
                </Suspense>,
                ssrFetchedData,
              )}
            </Routes>
          </div>
          <div
            className={`fixed bottom-0 z-10 w-screen ${
              isDashboardMobileSideMenuOpen ? 'pointer-events-none' : ''
            }`}
          >
            <DashboardBottomMobileMenu />
          </div>
        </div>
      </div>
    </DashboardLayout>
  ) : (
    <div className="flex min-h-screen min-w-full flex-col items-center justify-center">
      <Spinner color="fill-sky-600" />
    </div>
  );
};

export default Dashboard;
